import {
  type ChangeEvent,
  type DragEvent,
  type FC,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import AlertCircle from '@crehana/compass.icons.outline/AlertCircle';
import FileText from '@crehana/compass.icons.outline/FileText';
import Refresh from '@crehana/compass.icons.outline/Refresh';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Loader } from '@crehana/ui';
import { bytesToSize } from '@crehana/utils';

interface FileInputProps {
  accept?: string;
  file?: File;
  isDark?: boolean;
  label?: string | ReactNode;
  labelError?: string;
  labelErrorExtension?: string;
  labelErrorExtensionDesc?: string;
  labelLoading?: string;
  labelMaxSize?: string;
  labelReplaceFile?: string;
  maxSize?: number;
  onLoadFile?: (file: File) => void;
}

const FileInput: FC<React.PropsWithChildren<FileInputProps>> = ({
  accept = '.xlsx',
  file,
  isDark,
  label = 'Subir archivo',
  labelError,
  labelErrorExtension,
  labelErrorExtensionDesc,
  labelLoading = 'Cargando...',
  labelReplaceFile = 'Elige otro archivo',
  labelMaxSize,
  maxSize = 500,
  onLoadFile,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState('');
  const [errorExtension, setErrorExtension] = useState('');

  const handleState = (isError?: boolean) => {
    setIsLoading(false);
    setError(isError ? labelError || 'error' : '');
    setFileName('');
    setFileSize(0);
  };

  const handleSetErrorExtension = (isError?: boolean) => {
    setIsLoading(false);
    setErrorExtension(isError ? labelErrorExtension || 'error' : '');
    setFileName('');
    setFileSize(0);
  };

  const getExtensionFile = (nameFile: string) => {
    return nameFile.split('.')?.pop()?.toLowerCase();
  };

  const handleOnChange = (
    event?: ChangeEvent<HTMLInputElement>,
    drag?: DragEvent<HTMLLabelElement>,
  ) => {
    drag?.preventDefault();

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!event && !drag && !file) return;

    const fileDragged = drag
      ? drag.dataTransfer.files && drag?.dataTransfer.files[0]
      : file || null;

    const fileSelected =
      event && event.target.files?.length ? event.target.files[0] : file;

    const attachment = fileSelected || fileDragged;

    setIsLoading(true);
    setError('');
    setFileName(attachment?.name || '');
    setFileSize(attachment?.size || 0);

    if (attachment) {
      const size = attachment.size / 1024;
      const extension = getExtensionFile(attachment.name);

      if (size > maxSize) {
        handleState();
      } else if (extension !== 'xlsx') {
        handleSetErrorExtension(true);
      } else {
        setIsLoading(false);
        onLoadFile && onLoadFile(attachment);
      }
      // biome-ignore lint/style/useBlockStatements: single-line early return
    } else handleState();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (file) handleOnChange();
  }, [file]);

  const bodyLabel = `tw-font-body5 ${isDark ? 'text-white' : 'text-base-dark'}`;
  const legalsLabel = 'font-legals text-base-lighter';
  const errorLabel = `font-legals ${
    isDark ? 'text-red-light' : 'text-red-main'
  }`;

  return (
    <>
      <label
        htmlFor="input-file"
        className="tw-cursor-pointer tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-h-[180px] tw-bg-transparent tw-px-0.4 tw-text-center tw-rounded-2 tw-border-[2px] tw-border-dashed tw-border-neutral-light-400 hover:tw-border-neutral-light-500 tw-transition-colors tw-ease-in-out tw-duration-100"
        onDragEnter={e => handleOnChange(undefined, e)}
        onDragLeave={e => handleOnChange(undefined, e)}
        onDragOver={e => handleOnChange(undefined, e)}
        onDrop={e => handleOnChange(undefined, e)}
      >
        {fileName &&
          (isLoading ? (
            <>
              <Loader size={32} white={isDark} />
              <span className={bodyLabel}>{fileName}</span>
              <span className={legalsLabel}>{labelLoading}</span>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <FileText
                  size={20}
                  className="tw-text-neutral-light-600 tw-mr-0.4"
                />
                <p className={bodyLabel}>{fileName}</p>
              </div>
              <span className={legalsLabel}>{bytesToSize(fileSize)}</span>
              <p className="tw-text-link-s tw-text-link-icon tw-pt-1.2">
                <Refresh size={20} />
                {labelReplaceFile}
              </p>
            </>
          ))}
        {!fileName && (
          <>
            {errorExtension ? (
              <div>
                <AlertCircle
                  size={20}
                  className="tw-text-[#E6215D] tw-mb-0.8"
                />
                <p className="font-subtitle3 tw-text-[#E6215D] tw-font-semibold">
                  {errorExtension}
                </p>
                <p className="font-body4 tw-text-[#E6215D]">
                  {labelErrorExtensionDesc}
                </p>
                <p className="tw-text-link-s tw-text-link-icon tw-pt-1.2">
                  <Refresh size={20} />
                  {labelReplaceFile}
                </p>
              </div>
            ) : (
              <>
                <div className="tw-mb-0.8">{label}</div>
                <span className={legalsLabel}>{labelMaxSize}</span>
                {error && <span className={errorLabel}>{error}</span>}
              </>
            )}
          </>
        )}
      </label>
      <input
        type="file"
        id="input-file"
        accept={accept}
        className="hidden"
        onChange={event => handleOnChange(event)}
      />
    </>
  );
};

export default FileInput;
